import * as React from 'react';

type EventHandler<TEvent> = (e: TEvent) => void;
const listeners: { [index: string]: Array<EventHandler<any>> } = {};

type Props = {
  className?: string;
  children?: React.ReactNode;
};

// This component is needed to bind events to the most outer
// component but still use Reacts event system. If we were to
// bind directly to `document` you won't be able to stop the
// propagation to those handlers from inside React, since React
// only binds one handler to `document`.
export default (props: Props) => {
  const rootProps = {
    ...props,
    onClick: triggerEvent,
    onKeyDown: triggerEvent,
  };

  return <div {...rootProps} />;
};

export type ScopeSetScrollEvent = {
  type: 'scope:setscroll';
  position: number;
};

type RootEvent =
  | 'click'
  | 'keydown'
  | 'resize'
  | 'scroll'
  | 'touchmove'
  | 'touchstart'
  | 'touchend'
  | 'scope:setscroll';

export function on(event: 'click', callback: EventHandler<React.MouseEvent<HTMLElement>>): () => void;
export function on(event: 'keydown', callback: EventHandler<React.KeyboardEvent<HTMLElement>>): () => void;
export function on(event: 'resize' | 'scroll', callback: EventHandler<Event>): () => void;
export function on(event: 'touchstart' | 'touchmove' | 'touchend', callback: EventHandler<TouchEvent>): () => void;
export function on(event: 'scope:setscroll', callback: EventHandler<ScopeSetScrollEvent>): () => void;

export function on<TEvent>(event: RootEvent, callback: EventHandler<TEvent>) {
  const events = event.split(' ');
  events.forEach(eventName => {
    if (!listeners[eventName]) {
      listeners[eventName] = [];
    }

    listeners[eventName].push(callback);
    handleWindowSubscription(eventName);
  });

  // We return a function here that you can call to unsubscribe to the event.
  // This instead of exporting an `off` function where you have to pass
  // the same function you passed to `on` which makes it impossible to
  // pass an inline function to `on`.
  return () =>
    events.forEach(eventName => {
      listeners[eventName] = listeners[eventName].filter(listener => listener !== callback);
      handleWindowSubscription(eventName);
    });
}

export function scrollPosition(newPosition?: number) {
  if (newPosition !== undefined) {
    document.body.scrollTop = newPosition;
    document.documentElement.scrollTop = newPosition;

    triggerEvent({ type: 'scope:setscroll', position: newPosition } as ScopeSetScrollEvent);
  }
  return Math.max(document.body.scrollTop, document.documentElement.scrollTop);
}

export function scrollTo(to: number, duration: number) {
  const startTime = Date.now();
  const endTime = startTime + duration;

  const startTop = scrollPosition();
  const distance = to - startTop;

  const smoothStep = (start: number, end: number, point: number) => {
    if (point <= start) {
      return 0;
    }
    if (point >= end) {
      return 1;
    }
    const x = (point - start) / (end - start);
    return x * x * (3 - 2 * x);
  };

  const onScroll = (e: Event) => e.stopPropagation();

  return new Promise((resolve, reject) => {
    let previousTop = scrollPosition();
    const scrollFrame = () => {
      if (scrollPosition() !== previousTop) {
        return resolve();
      }

      const now = Date.now();
      const point = smoothStep(startTime, endTime, now);
      const frameTop = Math.round(startTop + distance * point);
      scrollPosition(frameTop);

      if (now >= endTime) {
        return resolve();
      }

      if (scrollPosition() === previousTop && scrollPosition() !== frameTop) {
        return resolve();
      }
      previousTop = scrollPosition();
      requestAnimationFrame(scrollFrame);
    };

    // We don't let anyone else notice the scroll events because
    // we want a smooth animation
    window.addEventListener('scroll', onScroll, true);
    requestAnimationFrame(scrollFrame);
  }).then(() => {
    window.removeEventListener('scroll', onScroll, true);
  });
}

export function one(event: 'click', callback: EventHandler<React.MouseEvent<HTMLElement>>): void;
export function one(event: 'keydown', callback: EventHandler<React.KeyboardEvent<HTMLElement>>): void;
export function one(event: 'resize' | 'scroll', callback: EventHandler<Event>): void;
export function one(event: 'touchstart' | 'touchmove' | 'touchend', callback: EventHandler<TouchEvent>): void;
export function one(event: 'scope:setscroll', callback: EventHandler<ScopeSetScrollEvent>): void;

export function one<TEvent>(event: RootEvent, callback: EventHandler<TEvent>) {
  const unsubscribe = on(event as any, (e: any) => {
    callback(e);
    unsubscribe();
  });
}

export function offsetTop(element: HTMLElement): number {
  if (!element) {
    return 0;
  }
  return element.offsetTop + offsetTop(element.offsetParent as HTMLElement);
}

function hasSubscribers(eventName: string) {
  return listeners[eventName] && listeners[eventName].length > 0;
}

function triggerEvent<T extends { type: string }>(e: T) {
  if (!hasSubscribers(e.type)) {
    return;
  }
  listeners[e.type].forEach(listener => {
    listener(e);
  });
}

const throttling: string[] = [];

// Some events than can fire at a high rate and execute computationally
// expensive operations such as DOM modifications. Those events should
// be throttled, such as `scroll` and `resize`.
function throttleEvent<T extends React.SyntheticEvent<HTMLElement>>(e: T) {
  if (!hasSubscribers(e.type) || throttling.indexOf(e.type) !== -1) {
    return;
  }
  throttling.push(e.type);
  window.requestAnimationFrame(() => {
    triggerEvent(e);
    throttling.splice(throttling.indexOf(e.type), 1);
  });
}

const windowEvents = {
  resize: throttleEvent,
  scroll: throttleEvent,
  // We need `touchmove` on the window because you can't swipe out
  // the drawer/mini cart
  touchmove: throttleEvent,
  touchstart: throttleEvent,
  touchend: throttleEvent,
};

const boundWindowEvents: string[] = [];

// Pass `window` events defined in `windowEvents` to this module. Should be
// invoked when `listeners` has been manipulated
function handleWindowSubscription(eventName: string) {
  if (windowEvents[eventName]) {
    if (hasSubscribers(eventName) && !boundWindowEvents.includes(eventName)) {
      window.addEventListener(eventName, windowEvents[eventName]);
      boundWindowEvents.push(eventName);
    } else if (!hasSubscribers(eventName) && boundWindowEvents.includes(eventName)) {
      window.removeEventListener(eventName, windowEvents[eventName]);
      boundWindowEvents.splice(boundWindowEvents.indexOf(eventName), 1);
    }
  }
}
